import { useCallback, useEffect, useRef, useState } from 'react';
import type { MyWorkResponse } from '../../lib/types/myWork';
import { shouldRefreshMyWork } from '../../lib/myWork/labels';
import { useApiFetch } from '../../hooks/useApiFetch';

export type MyWorkLoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly response: MyWorkResponse };

export interface MyWorkCountValue {
  readonly state: MyWorkLoadState;
  readonly total: number | null;
  readonly reload: () => Promise<void>;
}

export function useMyWork(active: boolean): MyWorkCountValue {
  const api = useApiFetch();
  const [state, setState] = useState<MyWorkLoadState>({ status: 'loading' });
  const lastFetchedAt = useRef(0);

  const reload = useCallback(async () => {
    if (!active) return;
    setState((current) => (current.status === 'ready' ? current : { status: 'loading' }));
    try {
      const response = await api('/my-work');
      if (!response) return;
      if (!response.ok) {
        setState({ status: 'error' });
        return;
      }
      const body = (await response.json()) as MyWorkResponse;
      lastFetchedAt.current = Date.now();
      setState({ status: 'ready', response: body });
    } catch {
      setState({ status: 'error' });
    }
  }, [active, api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!active) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!shouldRefreshMyWork(lastFetchedAt.current, Date.now())) return;
      void reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [active, reload]);

  const total = state.status === 'ready' ? state.response.total : null;
  return { state, total, reload };
}
